/**
 * @fileoverview Hook Data Layer para cargar mensajes paginados de conversación en AreaChatWindowMC.
 * Permite solicitar mensajes de una conversación y página específica recibiendo el objeto conversation y el número de página.
 * Cumple arquitectura Mercately: separación de capas, memoización y performance.
 * @module useAreaChatWindowMessagesLoader
 * @category Hook - Data Layer
 * @since 1.0.0
 * @version 1.1.0 - loadMessages ahora recibe (conversation, page)
 */

import { useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  fetchConversationMessages,
  fetchRetrieveMiaSources
} from "../../../../actions/inbox/conversationActions";

/**
 * Hook Data Layer para cargar mensajes de una conversación y página específica.
 *
 * - Solo gestiona la carga de datos (Data Layer)
 * - No mezcla lógica de UI ni de negocio
 * - Optimizado con useCallback
 *
 * @function useAreaChatWindowMessagesLoader
 * @returns {Function} loadMessages - Función para cargar mensajes de una página específica
 * @see AreaChatWindowMC
 * @example
 * const loadMessages = useAreaChatWindowMessagesLoader();
 * loadMessages(conversation, 2); // Carga la página 2 de la conversación
 */
export default function useAreaChatWindowMessagesLoader() {
  const dispatch = useDispatch();

  /**
   * Carga los mensajes de la conversación para la página indicada.
   *
   * @function loadMessages
   * @memberof useAreaChatWindowMessagesLoader
   * @param {Object} conversation - Objeto de conversación (debe tener al menos id y platform)
   * @param {string|number} conversation.id - ID de la conversación
   * @param {string} conversation.platform - Plataforma de la conversación
   * @param {number} [page=1] - Número de página a cargar (por defecto 1)
   * @returns {void}
   *
   * @example
   * // Cargar la primera página de mensajes
   * loadMessages(conversation);
   *
   * // Cargar la página 3 de mensajes
   * loadMessages(conversation, 3);
   */
  const loadMessages = useCallback(async(conversation, page = 1) => {
    if (!conversation?.id) return;
    const data = await dispatch(fetchConversationMessages(conversation.id, {
      page,
      platform: conversation.platform
    }));

    // llamar a retrieve_mia_sources
    if (data?.length > 0) {
      dispatch(fetchRetrieveMiaSources({
        message_ids: data.map(msg => msg.id).join(','),
        customer_id: conversation.customerId,
        platform: conversation.platform
      }));
    }
  }, [dispatch]);

  return loadMessages;
}
